import { useEffect, useState } from 'react';
import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { ChartNoAxesColumn } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DataTableProps } from '@/lib/types';
import { BULK_HEADER_CELL, BulkHeaderBar } from '@/components/glitch-ui/bulk-select';
import { DataTablePagination } from './pagination';
import { SortableHeader } from './sortable-header';

export function DataTable<TData>({
  data,
  columns,
  pageSize = 5,
  pageSizeOptions,
  onPageSizeChange,
  paginationPosition = 'bottom',
  onRowClick,
  itemsName,
  actionButtonText,
  onActionButtonClick,
  initialSorting,
  variant = 'bordered',
  emptyState,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyDescription,
  footerContent,
  getRowClassName,
  getRowId,
  bulk,
}: DataTableProps<TData> & { getRowId?: (row: TData, index: number) => string }) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  // A page that lets the reader choose rows-per-page (Search Terms, #6758) holds
  // the value and passes it down, so the prop has to be followed rather than
  // just seeding the first render. Back to page 1 on a change: the row at the
  // top of page 4 of 10 is not the row at the top of page 4 of 50.
  useEffect(() => {
    setPagination((prev) => (prev.pageSize === pageSize ? prev : { pageIndex: 0, pageSize }));
  }, [pageSize]);

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    // Optional stable row id — without this, TanStack uses the array index,
    // which causes React to reuse DOM rows with new data on delete. Cells
    // that hold local useState (e.g. inline edit ``localVal``) then show
    // stale text and the last DOM row appears to be the one removed. #4625.
    getRowId,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  const isFlush = variant === 'flush';
  const wrapperClassName = isFlush
    ? '-mx-6 -mb-6 overflow-x-auto rounded-b-lg'
    : 'bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden';

  if (data.length === 0 && EmptyIcon && emptyTitle) {
    return (
      // A flush table is already inside its card, so its empty state is not a
      // second box (#6715).
      <div className={isFlush ? 'bg-white' : 'border rounded-lg bg-white overflow-hidden'}>
        <div className='flex flex-col items-center justify-center py-16 px-6 text-center'>
          <EmptyIcon className='h-12 w-12 text-gray-500 mb-4' strokeWidth={1} />
          <h3 className='text-lg font-semibold text-gray-500 mb-2'>{emptyTitle}</h3>
          {emptyDescription && <p className='text-gray-500 max-w-sm'>{emptyDescription}</p>}
        </div>
      </div>
    );
  }

  const bulkActive = !!bulk && bulk.count > 0;

  // Page controls — without these every overflowing table was silently stuck on
  // page 1 (the search-terms report showed 25 of 360 with no way forward).
  // Single-page tables render none, unless a smaller rows-per-page would have
  // paged them: otherwise choosing 100 hides the control that chooses 10.
  const totalRows = table.getFilteredRowModel().rows.length;
  const showPageControls =
    table.getPageCount() > 1 ||
    (!!pageSizeOptions?.length && totalRows > Math.min(...pageSizeOptions));
  // Drawn at the foot, and at the head too when a table asks for it (#6758): a
  // long table is paged from whichever end the reader has scrolled to, so making
  // them travel back to the other one to turn the page is the thing to avoid.
  // The rule always sits between the controls and the rows.
  const pageControls = (edge: 'top' | 'bottom') =>
    !showPageControls ? null : (
    <div
      className={cn(
        'px-6',
        isFlush ? undefined : edge === 'top' ? 'border-b border-gray-200' : 'border-t border-gray-200',
      )}
    >
      <DataTablePagination
        pageIndex={table.getState().pagination.pageIndex}
        pageCount={table.getPageCount()}
        pageSize={table.getState().pagination.pageSize}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
        itemsName={itemsName}
        totalItems={table.getFilteredRowModel().rows.length}
        currentItems={table.getRowModel().rows.length}
        // The leading bar carries it when there are two; with only one bar,
        // that one does — every table that draws a single bar is unchanged.
        showRange={edge === 'top' || paginationPosition !== 'both'}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );

  return (
    <div className={cn(wrapperClassName, 'relative')}>
      {paginationPosition === 'both' && pageControls('top')}
      {/* Ticking rows covers the headings with the bar that acts on them
          (#6724). It lies over the head rather than replacing those cells,
          because the headings are what give the columns their widths: take
          them out and a long title re-wraps and every row below it moves. */}
      {bulkActive && bulk && (
        <div className={cn('absolute inset-x-0 top-0 z-10 flex items-center gap-3 bg-white', BULK_HEADER_CELL)}>
          {/* The select-all box is drawn again here, in its own column's
              padding, because the bar covers the head that holds the real
              one: the reader keeps one place to tick and untick everything. */}
          {(() => {
            const first = table.getHeaderGroups()[0]?.headers[0];
            if (!first) return null;
            const firstClass = (first.column.columnDef.meta as { className?: string } | undefined)?.className;
            return (
              <span className={cn('flex flex-shrink-0 items-center', firstClass ?? 'px-6')}>
                {flexRender(first.column.columnDef.header, first.getContext())}
              </span>
            );
          })()}
          <div className='min-w-0 flex-1'>
            <BulkHeaderBar
              count={bulk.count}
              itemName={bulk.itemName}
              actions={bulk.actions}
              onClear={bulk.onClear}
            />
          </div>
        </div>
      )}
      <div className={isFlush ? '' : 'overflow-x-auto'}>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-white'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const metaClassName = (header.column.columnDef.meta as { className?: string } | undefined)?.className;
                  return (
                  <th key={header.id} colSpan={header.colSpan} className={cn('whitespace-nowrap py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider', metaClassName ?? 'px-6')}>
                    {header.isPlaceholder ? null : (
                      typeof header.column.columnDef.header === 'string' && header.column.getCanSort() ? (
                        <SortableHeader column={header.column}>
                          {header.column.columnDef.header}
                        </SortableHeader>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )
                    )}
                  </th>
                  );
                })}
                {actionButtonText && onActionButtonClick && (
                  <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    <Button
                      onClick={onActionButtonClick}
                      variant='secondary'
                      className='text-white bg-[#290BD9] hover:bg-[#2309C5]'
                    >
                      {actionButtonText}
                    </Button>
                  </th>
                )}
              </tr>
            ))}
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn(
                    onRowClick ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50',
                    getRowClassName?.(row.original),
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const metaClassName = (cell.column.columnDef.meta as { className?: string } | undefined)?.className;
                    return (
                    <td key={cell.id} className={cn('py-4 whitespace-nowrap', metaClassName ?? 'px-6')}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                {/* The card empty idiom (EmptyChart's face): a muted icon over
                    the message, instead of a bare line lost in the table body. */}
                <td colSpan={columns.length} className='px-6 py-12 text-center'>
                  <div className='flex flex-col items-center justify-center'>
                    <ChartNoAxesColumn className='mb-3 h-10 w-10 text-gray-300' strokeWidth={1} />
                    <p className='text-sm text-gray-400'>{emptyState ?? 'No results.'}</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          {footerContent && (
            <tfoot className='bg-gray-50 border-t border-gray-200'>
              {footerContent}
            </tfoot>
          )}
        </table>
      </div>
      {pageControls('bottom')}
    </div>
  );
}
